import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Banner from '../components/common/Banner';
import SectionTitle from '../components/common/SectionTitle';
import Timeline from '../components/common/Timeline';
import Accordion from '../components/common/Accordion';
import CallToAction from '../components/common/CallToAction';
import { BookOpen, FileText, Users, Award, BookMarked, Microscope, BarChart, GraduationCap } from 'lucide-react';

const ServicesPage: React.FC = () => {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Update document title
    document.title = 'Our Services - VetEdu Consultants';
  }, []);

  // Timeline items for admission process
  const timelineItems = [
    {
      step: 1,
      title: 'Initial Consultation',
      description: 'We begin with a detailed consultation to understand your academic background, preferences, and aspirations.'
    },
    {
      step: 2,
      title: 'College Selection',
      description: 'Based on your NEET score and preferences, we help you identify the best veterinary colleges that match your profile.'
    },
    {
      step: 3,
      title: 'Application Preparation',
      description: 'We assist with preparing and organizing all necessary documents and application materials for selected colleges.'
    },
    {
      step: 4,
      title: 'Submission and Follow-up',
      description: 'Our team ensures timely submission of applications and follows up with colleges for updates and requirements.'
    },
    {
      step: 5,
      title: 'Interview Preparation',
      description: 'If required, we provide comprehensive interview preparation to help you present your best self to admission committees.'
    },
    {
      step: 6,
      title: 'Admission Confirmation',
      description: 'Once you receive offers, we help you evaluate options and complete the enrollment process at your chosen college.'
    }
  ];

  // FAQ items
  const faqItems = [
    {
      id: 'faq-1',
      title: 'When should I start the BVSc admission process?',
      content: (
        <p>
          The BVSc admission process typically begins after the NEET-UG results are announced, usually in June-July. However, we recommend contacting us even before your NEET exam for early guidance and preparation. This allows us to help you understand the process, prepare required documents, and create a strategy based on your expected scores.
        </p>
      )
    },
    {
      id: 'faq-2',
      title: 'What documents are required for BVSc admissions?',
      content: (
        <div>
          <p>The following documents are typically required for BVSc admissions:</p>
          <ul className="list-disc pl-5 mt-2">
            <li>NEET score card</li>
            <li>10th and 12th mark sheets and certificates</li>
            <li>Transfer certificate</li>
            <li>Character certificate</li>
            <li>Category certificate (if applicable)</li>
            <li>Domicile certificate</li>
            <li>Passport-sized photographs</li>
            <li>ID proof (Aadhar card, PAN card, etc.)</li>
            <li>Income certificate (if applicable for scholarships)</li>
          </ul>
          <p className="mt-2">Our documentation support service ensures all your documents are properly organized and verified.</p>
        </div>
      )
    },
    {
      id: 'faq-3',
      title: 'How does the counseling process work for veterinary admissions?',
      content: (
        <p>
          Veterinary admissions in India follow a counseling process similar to medical admissions. After qualifying in NEET, candidates must register for counseling conducted by respective authorities (like state veterinary universities or AIPVT). The counseling involves document verification, seat allotment based on merit and preferences, and payment of fees to confirm admission. Our counseling assistance service guides you through this entire process, helping with registration, choice filling, and seat confirmation.
        </p>
      )
    },
    {
      id: 'faq-4',
      title: 'What is the fee structure for BVSc courses in private and government colleges?',
      content: (
        <p>
          Fee structures vary significantly between government and private veterinary colleges. Government college fees typically range from ₹15,000 to ₹50,000 per year, while private college fees can range from ₹2 lakhs to ₹6 lakhs per year. Additionally, there may be hostel fees, mess charges, and other expenses. Our counselors provide detailed fee information for specific colleges you're interested in, helping you make informed financial decisions.
        </p>
      )
    },
    {
      id: 'faq-5',
      title: 'Do you guarantee admission to a veterinary college?',
      content: (
        <p>
          While we cannot guarantee admission as it ultimately depends on your NEET score, available seats, and other factors, we have a very high success rate. Our comprehensive approach, college networks, and expertise significantly increase your chances of securing admission. We work with multiple colleges across India and help you explore all possible options based on your profile and preferences.
        </p>
      )
    }
  ];

  return (
    <>
      <Banner 
        title="Our Services"
        subtitle="Comprehensive support for your veterinary education journey"
        backgroundImage="https://images.pexels.com/photos/339620/pexels-photo-339620.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
      />
      
      <section className="section">
        <div className="container-custom">
          <SectionTitle 
            title="Comprehensive Admission Services" 
            subtitle="We provide end-to-end support to help you secure admission in top veterinary colleges."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
            <div className="card">
              <div className="p-6 md:p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-6">
                  <Users size={32} />
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-primary-700">
                  Admission Counselling
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Personalized guidance to help you choose the right veterinary college based on your NEET score, preferences, budget, and career goals.
                </p>
                
                <Link 
                  to="/services/counselling" 
                  className="btn btn-primary w-full text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
            
            <div className="card">
              <div className="p-6 md:p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-6">
                  <FileText size={32} />
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-primary-700">
                  Application Assistance
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Complete support with the application process, including form filling, document preparation, and submission to multiple colleges.
                </p>
                
                <Link 
                  to="/services/application" 
                  className="btn btn-primary w-full text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
            
            <div className="card">
              <div className="p-6 md:p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-6">
                  <BookMarked size={32} />
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-primary-700">
                  Documentation Support
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Expert help with compiling and organizing all necessary documents required for BVSc admissions, ensuring nothing is missed.
                </p>
                
                <Link 
                  to="/services/document" 
                  className="btn btn-primary w-full text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
            
            <div className="card">
              <div className="p-6 md:p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-6">
                  <BookOpen size={32} />
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-primary-700">
                  Interview Preparation
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Comprehensive preparation for college interviews, including mock sessions and guidance on common veterinary admission questions.
                </p>
                
                <Link 
                  to="/services/interview" 
                  className="btn btn-primary w-full text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
            
            <div className="card">
              <div className="p-6 md:p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-6">
                  <Award size={32} />
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-primary-700">
                  Scholarship Guidance
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Information and assistance with scholarship applications to help make your veterinary education more affordable.
                </p>
                
                <Link 
                  to="/services/scholarship" 
                  className="btn btn-primary w-full text-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
            
            <div className="card">
              <div className="p-6 md:p-8">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mb-6">
                  <GraduationCap size={32} />
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-primary-700">
                  Post-Admission Support
                </h3>
                
                <p className="text-gray-600 mb-6">
                  Continued assistance after admission, including orientation guidance, accommodation support, and academic counseling.
                </p>
                
                <Link 
                  to="/contact" 
                  className="btn btn-primary w-full text-center"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Specializations in Veterinary Science" 
            subtitle="BVSc offers various specialization opportunities for students interested in specific areas."
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Microscope size={28} className="text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-primary-700">Veterinary Medicine</h3>
              <p className="text-gray-600 text-sm">
                Diagnosis and treatment of animal diseases and disorders in both pets and livestock.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText size={28} className="text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-primary-700">Veterinary Surgery</h3>
              <p className="text-gray-600 text-sm">
                Surgical interventions for animals, including orthopedic, soft tissue, and emergency procedures.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart size={28} className="text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-primary-700">Animal Nutrition</h3>
              <p className="text-gray-600 text-sm">
                Study of animal dietary needs, feed formulation, and nutritional management for optimal health.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen size={28} className="text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-primary-700">Wildlife Medicine</h3>
              <p className="text-gray-600 text-sm">
                Specialized care for wild animals, conservation medicine, and wildlife management.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section">
        <div className="container-custom">
          <SectionTitle 
            title="Our Admission Process" 
            subtitle="A step-by-step approach to ensure your successful admission to veterinary colleges."
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <Timeline items={timelineItems} />
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle="Common questions about veterinary admissions and our services."
          />
          
          <div className="max-w-3xl mx-auto mt-8">
            <Accordion items={faqItems} />
          </div>
        </div>
      </section>
      
      <CallToAction 
        title="Ready to Begin Your Veterinary Journey?"
        description="Contact us today for expert guidance on BVSc admissions and take the first step toward your dream career."
        buttonText="Contact Us"
        buttonLink="/contact"
      />
    </>
  );
};

export default ServicesPage;